import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { topics } from "@/mocks/topics";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";

export default function Live() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "intel" ? "intel" : "live";
  const cat = searchParams.get("cat") || "all";
  const [keyword, setKeyword] = useState("");

  const isIntel = tab === "intel";

  useSeo({
    title: isIntel
      ? "瓜田情报 - 看料大瓜 | 热点线索梳理与后续进展"
      : "现场直击 - 看料大瓜 | 实时追踪热点实况",
    description: isIntel
      ? "看料大瓜瓜田情报栏目，按线索归拢热点事件的碎片信息，标注核实状态与后续进展，帮你看清事件来龙去脉。"
      : "看料大瓜现场直击栏目，实时追踪热点实况，汇聚目击者亲历描述与事件时间线，按明星现场、城市现场等主题分类浏览。",
    keywords: "看料大瓜现场,看料大瓜直击,瓜田情报,吃瓜第一线,现场直击",
    canonical: isIntel
      ? "https://ahgslo.cn/live?tab=intel"
      : "https://ahgslo.cn/live",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: isIntel ? "瓜田情报" : "现场直击",
      description: "看料大瓜热点内容列表页。",
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ahgslo.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: isIntel ? "瓜田情报" : "现场直击",
            item: "https://ahgslo.cn/live",
          },
        ],
      },
    },
  });

  const list = useMemo(() => {
    let result = articles;
    if (cat !== "all") result = result.filter((item) => item.category === cat);
    const kw = keyword.trim();
    if (kw) {
      result = result.filter(
        (item) =>
          item.title.includes(kw) ||
          item.excerpt.includes(kw) ||
          item.tags.some((tag) => tag.includes(kw)),
      );
    }
    if (isIntel) {
      return [...result].sort((a, b) => b.views - a.views);
    }
    return result;
  }, [cat, keyword, isIntel]);

  const setTab = (next: "live" | "intel") => {
    const params = new URLSearchParams(searchParams);
    if (next === "intel") params.set("tab", "intel");
    else params.delete("tab");
    setSearchParams(params);
  };

  const setCat = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("cat");
    else params.set("cat", next);
    setSearchParams(params);
  };

  return (
    <main className="w-full">
      <div className="mx-auto max-w-[1240px] px-4 pt-6 md:px-6">
        <Breadcrumbs items={[{ label: isIntel ? "瓜田情报" : "现场直击" }]} />
      </div>

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
            {isIntel ? "INTEL FEED" : "LIVE COVERAGE"}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            {isIntel ? "瓜田情报" : "现场直击"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {isIntel
              ? "按线索归拢热点事件的碎片信息，标注核实状态，并持续更新后续进展。"
              : "以事件当天的时间线为骨架，记录正在发生的第一手实况与目击者描述。"}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              {[
                { key: "live", label: "现场直击" },
                { key: "intel", label: "瓜田情报" },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key as "live" | "intel")}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="relative flex-1 sm:max-w-xs">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索标题、标签或关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {topics.map((topic) => (
              <button
                key={topic.slug}
                type="button"
                onClick={() => setCat(topic.slug)}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                  cat === topic.slug
                    ? "border-primary-400 bg-primary-100 text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={topic.icon} />
                {topic.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共 <span className="font-medium text-foreground-900">{list.length}</span> 篇内容
            </p>
          </div>

          {list.length === 0 ? (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                <i className="ri-search-eye-line" />
              </span>
              <p className="mt-4 font-heading text-base font-bold text-foreground-800">
                没有找到匹配的内容
              </p>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词，或点击上方主题查看其他现场
              </p>
            </div>
          ) : isIntel ? (
            <div className="mt-8 flex flex-col gap-3">
              {list.map((article, index) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  variant="horizontal"
                  rank={index + 1}
                />
              ))}
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </section>
      <BackToTop />
    </main>
  );
}